import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../firebase';
import { LeaderboardEntry } from '../../types';
import './LeaderboardsView.css';

enum LiftType {
  BenchPress = "Bench Press",
  Deadlift = "Deadlift",
  Squat = "Squat",
  Clean = "Clean"
}

const LeaderboardsView: React.FC = () => {
  const [user] = useAuthState(auth);
  const [selectedLift, setSelectedLift] = useState<LiftType>(LiftType.BenchPress);
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    loadLeaderboard();
  }, [selectedLift]);

  const loadLeaderboard = async () => {
    setIsLoading(true);
    try {
      // Get all max lifts and find the latest for each user
      const maxLiftsRef = collection(db, 'maxLifts');
      const q = query(maxLiftsRef, orderBy('dateCreated', 'desc'), limit(100));
      
      const snapshot = await getDocs(q);
      const userMaxes = new Map<string, any>();
      
      // Get the most recent max lift for each user
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!userMaxes.has(data.userId)) {
          userMaxes.set(data.userId, data);
        }
      });

      // Get user profiles for display names
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const userProfiles = new Map<string, any>();
      
      usersSnapshot.docs.forEach(doc => {
        userProfiles.set(doc.id, doc.data());
      });

      // Create leaderboard entries
      const entries: LeaderboardEntry[] = Array.from(userMaxes.entries()).map(([userId, maxLift]) => {
        const userProfile = userProfiles.get(userId);
        const value = getLiftValue(maxLift, selectedLift);
        
        // Handle both web format (name) and iOS/Android format (firstName + lastName)
        let userName = 'Anonymous';
        if (userProfile?.name) {
          // Web format
          userName = userProfile.name;
        } else if (userProfile?.firstName || userProfile?.lastName) {
          // iOS/Android format
          const firstName = userProfile.firstName || '';
          const lastName = userProfile.lastName || '';
          userName = `${firstName} ${lastName}`.trim();
        }
        
        return {
          id: userId,
          userName: userName,
          userAvatar: userProfile?.avatar || '',
          value: value,
          rank: 0 // Will be set after sorting
        };
      }).filter(entry => entry.value > 0);

      // Sort and rank
      entries.sort((a, b) => b.value - a.value);
      entries.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      setLeaderboardData(entries);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error loading leaderboard:', error);
      setErrorMessage(`Failed to load leaderboard: ${error.message}`);
      setIsLoading(false);
    }
  };

  const getLiftValue = (maxLift: any, liftType: LiftType): number => {
    switch (liftType) {
      case LiftType.BenchPress: return maxLift.benchPress || 0;
      case LiftType.Deadlift: return maxLift.deadlift || 0;
      case LiftType.Squat: return maxLift.squat || 0;
      case LiftType.Clean: return maxLift.clean || 0;
    }
  };

  const getColorForLift = (type: LiftType): string => {
    switch (type) {
      case LiftType.BenchPress: return '#3b82f6';
      case LiftType.Deadlift: return '#ef4444';
      case LiftType.Squat: return '#10b981';
      case LiftType.Clean: return '#f59e0b';
    }
  };

  const getRankIcon = (rank: number): string => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const isCurrentUser = (entry: LeaderboardEntry): boolean => {
    return entry.id === user?.uid;
  };

  if (isLoading) {
    return (
      <div className="leaderboards-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboards-view">
      <div className="leaderboards-header">
        <h1>Leaderboards</h1>
        <div className="lift-selector">
          {Object.values(LiftType).map((liftType) => (
            <button
              key={liftType}
              className={`lift-tab ${selectedLift === liftType ? 'active' : ''}`}
              onClick={() => setSelectedLift(liftType)}
              style={{
                borderBottomColor: selectedLift === liftType ? getColorForLift(liftType) : 'transparent'
              }}
            >
              {liftType}
            </button>
          ))}
        </div>
      </div>

      {leaderboardData.length === 0 ? (
        <div className="empty-state">
          <div className="empty-content">
            <div className="empty-icon">🏆</div>
            <h2>No Data Yet</h2>
            <p>Be the first to set a {selectedLift} record!</p>
          </div>
        </div>
      ) : (
        <div className="leaderboard-content">
          <div className="current-lift-header">
            <h2 style={{ color: getColorForLift(selectedLift) }}>
              {selectedLift} Leaderboard
            </h2>
            <p>{leaderboardData.length} competitors</p>
          </div>

          <div className="leaderboard-list">
            {leaderboardData.map((entry) => (
              <div 
                key={entry.id}
                className={`leaderboard-entry ${isCurrentUser(entry) ? 'current-user' : ''}`}
              >
                <div className="rank-badge">
                  <span className="rank-text">
                    {getRankIcon(entry.rank)}
                  </span>
                </div>
                
                <div className="user-info">
                  <div className="user-avatar">
                    {entry.userAvatar ? (
                      <img src={entry.userAvatar} alt={entry.userName} />
                    ) : (
                      <div className="avatar-placeholder">
                        {entry.userName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="user-details">
                    <span className="user-name">
                      {entry.userName}
                      {isCurrentUser(entry) && <span className="you-badge">You</span>}
                    </span>
                  </div>
                </div>

                <div className="lift-value">
                  <span className="value">{entry.value}</span>
                  <span className="unit">lbs</span>
                </div>
              </div>
            ))}
          </div>

          {user && !leaderboardData.some(entry => isCurrentUser(entry)) && (
            <div className="user-not-ranked">
              <div className="not-ranked-message">
                <p>You haven't set a {selectedLift} record yet.</p>
                <p>Add your max lift to appear on the leaderboard!</p>
              </div>
            </div>
          )}
        </div>
      )}

      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default LeaderboardsView;
